"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { authClient } from "@repo/auth/client";
import { useAuthErrorMessages } from "@saas/auth/hooks/errors-messages";
import { useRouter } from "@shared/hooks/router";
import { useFormErrors } from "@shared/hooks/form-errors";
import { Alert, AlertDescription, AlertTitle } from "@ui/components/alert";
import { Button } from "@ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@ui/components/form";
import { Input } from "@ui/components/input";
import { AlertTriangleIcon, CheckCircle2Icon, MailboxIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { z } from "zod";

/**
 * Generate a cryptographically secure random password
 */
function generateSecurePassword(): string {
  const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
  let password = "";
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  for (let i = 0; i < 32; i++) {
    password += charset[array[i] % charset.length];
  }
  return password;
}

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
});

type FormValues = z.infer<typeof formSchema>;

interface InvitationSignupFormProps {
  invitationId: string;
  email: string;
  organizationName: string;
  organizationSlug: string;
  logoUrl?: string;
}

/**
 * Simplified signup form for invited users
 * Only asks for their name, auto-generates password
 */
export function InvitationSignupForm({
  invitationId,
  email,
  organizationName,
  organizationSlug,
  logoUrl,
}: InvitationSignupFormProps) {
  const t = useTranslations();
  const router = useRouter();
  const { zodErrorMap } = useFormErrors();
  const { getAuthErrorMessage } = useAuthErrorMessages();
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema, {
      errorMap: zodErrorMap,
    }),
    values: {
      name: "",
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async ({ name }) => {
    try {
      const password = generateSecurePassword();

      // Sign up with auto-generated password
      const { error } = await authClient.signUp.email({
        email,
        password,
        name,
        callbackURL: `/app/organization-invitation/${invitationId}`,
      });

      // If user already exists, send magic link instead
      if (error?.code === "USER_ALREADY_EXISTS") {
        const { error: magicLinkError } = await authClient.signIn.magicLink({
          email,
          callbackURL: `/app/organization-invitation/${invitationId}`,
        });

        if (magicLinkError) {
          throw magicLinkError;
        }

        setMagicLinkSent(true);
        return;
      }

      if (error) {
        throw error;
      }

      // Sign in the user (needed because autoSignIn may be disabled)
      const { error: signInError } = await authClient.signIn.email({
        email,
        password,
      });

      if (signInError) {
        throw signInError;
      }

      // Mark onboarding complete - invited users already have name and don't need demo content
      await authClient.updateUser({
        onboardingComplete: true,
      });

      // Accept invitation
      const { error: acceptError } = await authClient.organization.acceptInvitation({
        invitationId,
      });

      if (acceptError) {
        throw acceptError;
      }

      // Redirect to organization dashboard
      router.push(`/app/${organizationSlug}`);
    } catch (e) {
      form.setError("root", {
        message: getAuthErrorMessage(
          e && typeof e === "object" && "code" in e ? (e.code as string) : undefined
        ),
      });
    }
  };

  const isSubmitting = form.formState.isSubmitting;
  const isSuccessful = form.formState.isSubmitSuccessful;

  return (
    <div>
      {/* Organization Logo */}
      {logoUrl && (
        <div className="mb-6 flex justify-center">
          {logoUrl.startsWith("/image-proxy/") ? (
            // Use regular img tag for image-proxy URLs (which redirect to signed S3 URLs)
            // Next.js Image component doesn't handle redirects well
            // biome-ignore lint/performance/noImgElement: image-proxy URLs require native img tag
            <img
              src={logoUrl}
              alt={`${organizationName} logo`}
              className="max-h-16 w-auto object-contain"
            />
          ) : (
            <Image
              src={logoUrl}
              alt={`${organizationName} logo`}
              width={200}
              height={64}
              className="max-h-16 w-auto object-contain"
            />
          )}
        </div>
      )}

      <h1 className="font-bold text-xl md:text-2xl">{t("auth.invitation.title")}</h1>
      <p className="mt-1 mb-6 text-foreground/60">
        {t("auth.invitation.message", { organizationName })}
      </p>

      {magicLinkSent ? (
        <Alert variant="success">
          <MailboxIcon />
          <AlertTitle>{t("auth.login.hints.linkSent.title")}</AlertTitle>
          <AlertDescription>{t("auth.login.hints.linkSent.message")}</AlertDescription>
        </Alert>
      ) : isSuccessful ? (
        <Alert variant="success">
          <CheckCircle2Icon />
          <AlertTitle>{t("auth.invitation.success.title")}</AlertTitle>
          <AlertDescription>{t("auth.invitation.success.message")}</AlertDescription>
        </Alert>
      ) : (
        <Form {...form}>
          <form
            className="flex flex-col items-stretch gap-4"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {form.formState.isSubmitted && form.formState.errors.root && (
              <Alert variant="error">
                <AlertTriangleIcon />
                <AlertDescription>{form.formState.errors.root.message}</AlertDescription>
              </Alert>
            )}

            {/* Name input */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("auth.signup.name")}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t("auth.invitation.namePlaceholder")} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button loading={isSubmitting} disabled={isSubmitting}>
              {t("auth.invitation.submit")}
            </Button>
          </form>
        </Form>
      )}
    </div>
  );
}
